import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { DECLINED } from '@rsos/constants/peerAgenciesStatuses';
import normalizeError from '@rsos/sinatra/src/utils/normalizeError';
import { groupBy } from '@rsos/utils/groupBy';
import {
  getAgencies,
  getAgencyLinks,
  postAgency,
  patchAgency,
  deleteAgency,
} from './peerAgenciesAPI';

export const initialState = {
  agencyLinks: {
    error: null,
    linksFrom: [], // These are links from other agencies to mine, e.g. invites
    linksTo: [], // These are links to other agencies from mine, where I can share with them
    loading: false,
  },
  error: null,
  loading: false,
};

export const fetchAgencyLinks = createAsyncThunk(
  'peerAgencies/fetchAgencyLinks',
  async (orgName, { rejectWithValue }) => {
    try {
      const response = await getAgencyLinks(orgName);
      return response.data;
    } catch (error) {
      const { message } = normalizeError(error);
      return rejectWithValue(message);
    }
  },
);

export const fetchAgencies = createAsyncThunk(
  'peerAgencies/fetchAgencies',
  async (data, { rejectWithValue }) => {
    try {
      const response = await getAgencies(data);
      return response.data;
    } catch (error) {
      const { message } = normalizeError(error);
      return rejectWithValue(message);
    }
  },
);

export const addAgency = createAsyncThunk(
  'peerAgencies/addAgencies',
  async (data, { rejectWithValue }) => {
    try {
      const response = await postAgency(data);
      return response.data;
    } catch (error) {
      const { message } = normalizeError(error);
      return rejectWithValue(message);
    }
  },
);

export const updateAgency = createAsyncThunk(
  'peerAgencies/updateAgencies',
  async (data, { rejectWithValue }) => {
    try {
      const response = await patchAgency({
        org: data.org,
        id: data.id,
        status: data.status,
      });
      return response.data;
    } catch (error) {
      const { message } = normalizeError(error);
      return rejectWithValue(message);
    }
  },
);

export const removeAgency = createAsyncThunk(
  'peerAgencies/removeAgency',
  async (data, { rejectWithValue }) => {
    try {
      const response = await deleteAgency(data);
      return response.data;
    } catch (error) {
      const { message } = normalizeError(error);
      return rejectWithValue(message);
    }
  },
);

export const peerAgenciesSlice = createSlice({
  name: 'peerAgencies',
  initialState,
  reducers: {
    removeAgencyLinksTo: (state, action) => {
      const id = action.payload;
      state.agencyLinks.linksTo = state.agencyLinks.linksTo.filter(
        agency => agency.id !== id,
      );
    },
    removeDeclinedAgencyLinksTo: (state, action) => {
      state.agencyLinks.linksTo = state.agencyLinks.linksTo.filter(
        agency =>
          agency.status !== DECLINED && agency.link_to.id !== action.payload,
      );
    },
    setAgencyLinksFrom: (state, action) => {
      const { id, status } = action.payload;
      state.agencyLinks.linksFrom.filter(agency => {
        if (agency.id === id) {
          agency.status = status;
        }
      });
    },
    setPendingAgencyLinksTo: (state, action) => {
      // Context: In the `link to` peer agencies list, there can only be one declined or pending
      // request from any agency. If there is already a declined request in the list, and the sharer
      // follows up with another pending request, the existing declined request should be deleted
      // and replaced by the new pending request. This is needed because the GET `agency-link`
      // request only happens on the peer agencies dashboard page refresh.
      // The list of peer agencies status is grouped by the following statuses then sort by name:
      // active/deactivated > pending > declined
      const pendingAgency = action.payload;
      let agenciesByStatus = groupBy([...state.agencyLinks.linksTo], 'status');
      let sortedAgencies = [];

      agenciesByStatus.pending = agenciesByStatus.pending
        ? [...agenciesByStatus.pending, pendingAgency]
        : [pendingAgency];
      agenciesByStatus.pending.sort((a, b) =>
        a.link_to.display_name
          .toLowerCase()
          .localeCompare(b.link_to.display_name.toLowerCase()),
      );
      for (const status in agenciesByStatus) {
        sortedAgencies = [...sortedAgencies, agenciesByStatus[status]];
      }
      state.agencyLinks.linksTo = sortedAgencies.flat();
    },
    toggleAgencyStatus: (state, action) => {
      const { id, status } = action.payload;
      state.agencyLinks.linksFrom.filter(agency => {
        if (agency.id === id) {
          agency.status = status;
        }
      });
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchAgencyLinks.pending, state => {
        state.agencyLinks.loading = true;
      })
      .addCase(fetchAgencyLinks.fulfilled, (state, action) => {
        const response = action.payload;
        state.agencyLinks.loading = false;
        state.agencyLinks.linksFrom = response?.links_from;
        state.agencyLinks.linksTo = response?.links_to;
      })
      .addCase(fetchAgencyLinks.rejected, (state, action) => {
        state.agencyLinks.loading = false;
        state.agencyLinks.error = action.payload;
      })
      .addCase(fetchAgencies.pending, state => {
        state.loading = true;
      })
      .addCase(fetchAgencies.fulfilled, state => {
        state.loading = false;
      })
      .addCase(fetchAgencies.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(addAgency.pending, state => {
        state.loading = true;
      })
      .addCase(addAgency.fulfilled, state => {
        state.loading = false;
      })
      .addCase(addAgency.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateAgency.pending, state => {
        state.loading = true;
      })
      .addCase(updateAgency.fulfilled, state => {
        state.loading = false;
      })
      .addCase(updateAgency.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(removeAgency.pending, state => {
        state.loading = true;
      })
      .addCase(removeAgency.fulfilled, state => {
        state.loading = false;
      })
      .addCase(removeAgency.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const {
  removeAgencyLinksTo,
  removeDeclinedAgencyLinksTo,
  setAgencyLinksFrom,
  setPendingAgencyLinksTo,
  toggleAgencyStatus,
} = peerAgenciesSlice.actions;

export default peerAgenciesSlice.reducer;
